import {
	createContext,
	FC,
	MutableRefObject,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';

import { FullPageError } from 'src/components/full-page-error/FullPageError';
import { Logger } from 'src/services/Logger';

import { isLoggedIn, isUserAuthProviderSaml } from '../../services/AuthenticationService';
import { getUserTenant } from '../../services/TenantsService';
import { getUserMetadata } from '../../services/UserService';
import { getRolesList } from '../../services/UsersService';
import { ITenant, ProductType, TenantProduct } from '../../types/Tenant';
import { IUser, RoleTypeItem } from '../../types/User';

import { runWithLoadingLock } from './UserTenantProvider.helpers';
import { USER_TENANT_PROVIDER_LABELS } from './UserTenantProvider.labels';

interface IUsersRolesListDictionary {
	[key: string]: string;
}

interface ILoadWithErrorStateParams<T> {
	shouldLoad: boolean;
	lockRef: MutableRefObject<boolean>;
	load: () => Promise<T>;
	errorMessage: string;
	onLoaded: (loadedValue: T) => void;
}

interface IUserTenant {
	user: IUser | null;
	tenant: ITenant | null;
	hasAdminPermissions: () => boolean;
	updateUserTenant: (arg1?: boolean) => Promise<void>;
	usersRolesList: RoleTypeItem[];
	setUsersRolesList: (usersRolesList: RoleTypeItem[]) => void;
	getFullRoleItem: (role: string) => RoleTypeItem;
	usersRolesListDictionary: IUsersRolesListDictionary;
	getProductIdByType: (type: ProductType) => string;
}

const UserTenantContext = createContext<IUserTenant | null>(null);
const MISSING_ROLE_NAME_PREFIX = 'Unknown role';

export const UserTenantProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const [user, setUser] = useState<IUser | null>(null);
	const [tenant, setTenant] = useState<ITenant | null>(null);
	const [usersRolesList, setUsersRolesList] = useState<RoleTypeItem[]>([]);
	const [usersRolesListDictionary, setusersRolesListDictionary] =
		useState<IUsersRolesListDictionary>({});
	const [hasLoadingError, setHasLoadingError] = useState(false);

	const isUserLoading = useRef<boolean>(false);
	const isTenantLoading = useRef<boolean>(false);
	const isRolesLoading = useRef<boolean>(false);

	const getProductIdByType = useCallback(
		(productType: ProductType): string => {
			const product = tenant?.products?.find(
				(product: TenantProduct) => product.type === productType
			);
			return product?.id || '';
		},
		[tenant]
	);

	const getFullRoleItem = useCallback(
		(role: string): RoleTypeItem => {
			const roleItem = usersRolesList.find((item) => item.type === role);
			if (roleItem) {
				return roleItem;
			}

			Logger.warn('Role type not found in roles list', { role });
			return { id: role, name: `${MISSING_ROLE_NAME_PREFIX}: ${role}`, type: role };
		},
		[usersRolesList]
	);

	const loadWithErrorState = useCallback(
		async <T,>({
			shouldLoad,
			lockRef,
			load,
			errorMessage,
			onLoaded
		}: ILoadWithErrorStateParams<T>): Promise<void> => {
			if (!shouldLoad) {
				return;
			}

			try {
				const loadedValue = await runWithLoadingLock({
					lockRef,
					load,
					errorMessage
				});

				if (loadedValue) {
					onLoaded(loadedValue);
				}
			} catch (error) {
				setHasLoadingError(true);
				throw error;
			}
		},
		[]
	);

	const updateUserTenant = useCallback(
		async (force = false): Promise<void> => {
			if (!isLoggedIn()) {
				return;
			}

			setHasLoadingError(false);
			await loadWithErrorState({
				shouldLoad: !user || force,
				lockRef: isUserLoading,
				load: getUserMetadata,
				errorMessage: 'Failed to load user metadata',
				onLoaded: (currentUser) => {
					const isSSO = isUserAuthProviderSaml();
					setUser({ ...currentUser, isSSO });
				}
			});
			await loadWithErrorState({
				shouldLoad: !tenant || force,
				lockRef: isTenantLoading,
				load: getUserTenant,
				errorMessage: 'Failed to load user tenant',
				onLoaded: setTenant
			});
			await loadWithErrorState({
				shouldLoad: !usersRolesList.length || force,
				lockRef: isRolesLoading,
				load: getRolesList,
				errorMessage: 'Failed to load user roles list',
				onLoaded: setUsersRolesList
			});
		},
		[loadWithErrorState, user, tenant, usersRolesList]
	);

	const onLoad = useCallback(() => updateUserTenant(), [updateUserTenant]);

	const hasAdminPermissions = useCallback(
		(): boolean => !!user && user.role?.type === usersRolesListDictionary?.TENANT_ADMIN,
		[user, usersRolesListDictionary]
	);

	useEffect(() => {
		onLoad().catch(() => {
			// The provider already logged and exposed the error state.
		});
	}, [onLoad]);

	useEffect(() => {
		const typesListEnum: IUsersRolesListDictionary = {};
		usersRolesList.forEach((role) => {
			typesListEnum[role.type] = role.type;
		});
		setusersRolesListDictionary(typesListEnum);
	}, [usersRolesList]);

	const value = useMemo(() => {
		return {
			user,
			tenant,
			hasAdminPermissions,
			updateUserTenant,
			usersRolesList,
			setUsersRolesList,
			getFullRoleItem,
			usersRolesListDictionary,
			getProductIdByType
		};
	}, [
		user,
		tenant,
		hasAdminPermissions,
		updateUserTenant,
		usersRolesList,
		getFullRoleItem,
		usersRolesListDictionary,
		getProductIdByType
	]);
	if (hasLoadingError) {
		return <FullPageError title={USER_TENANT_PROVIDER_LABELS.loadingErrorTitle} />;
	}

	return <UserTenantContext.Provider value={value}>{children}</UserTenantContext.Provider>;
};

export const useUserTenant = (): IUserTenant => {
	const context = useContext(UserTenantContext);

	if (!context) {
		throw new Error('useUserTenant must be used within UserTenantProvider');
	}

	return context;
};
